/**
 * The Agents view of Socials: the work as seven steps, what each did, what it may and may not do, and the rules learned
 * from the person's decisions.
 *
 * Pure. The worker gathers a few facts from its own records and this turns them into the words on screen. Nothing is
 * invented: a step with nothing behind it says so, an unknown cost is "Not available", and steps that are not automated
 * say plainly that a person does that work.
 */
import { z } from "zod";
import { agentRunSchema, agentStateSchema, type AgentRun } from "./adsAgents.ts";
import { MIN_GROUP_POSTS } from "./socialInsights.ts";
import type { PastDecision } from "./socialReview.ts";
import type { PostFormat, PostHook } from "./socialStage.ts";

export const SOCIAL_STEP_IDS = ["ideas", "learn", "write", "pictures", "review", "send", "results"] as const;
export const socialStepIdSchema = z.enum(SOCIAL_STEP_IDS);
export type SocialStepId = (typeof SOCIAL_STEP_IDS)[number];

export const socialStepSchema = z.object({
  id: socialStepIdSchema,
  title: z.string(),
  who: z.string(),
  state: agentStateSchema,
  did: z.string(),
  cost: z.string(),
  limits: z.array(z.string()),
  runs: z.array(agentRunSchema),
  action: z.object({ label: z.string(), to: z.enum(["posts", "review", "channels", "results", "connections"]) }).nullable(),
});
export type SocialStep = z.infer<typeof socialStepSchema>;

export const socialRuleSchema = z.object({
  key: z.string(),
  text: z.string(),
  /** Decisions this rule is based on. */
  count: z.number().int(),
  on: z.boolean(),
});
export type SocialRule = z.infer<typeof socialRuleSchema>;

export const socialAgentsSchema = z.object({
  steps: z.array(socialStepSchema),
  rules: z.array(socialRuleSchema),
  decisions: z.object({ approved: z.number().int(), notForUs: z.number().int() }),
  waiting: z.string().nullable(),
});
export type SocialAgents = z.infer<typeof socialAgentsSchema>;

export const switchSocialRuleBodySchema = z.object({ siteId: z.string().min(1), key: z.string().regex(/^(hook|format):[A-Za-z +]{1,20}$/), on: z.boolean() });

const HOOK_PHRASE: Record<PostHook, string> = { Number: "open with a number", Question: "open with a question", Statement: "open with a statement" };
const FORMAT_PHRASE: Record<PostFormat, string> = { Text: "are text only", "Text + image": "have a picture", Video: "have a video" };

/**
 * One rule for each way of opening and each format with at least three decisions behind it, when you clearly liked it
 * (approved 75% or more) or clearly did not (34% or less). Anything in between says nothing, so no rule is written.
 */
export function learnSocialRules(history: readonly PastDecision[], off: readonly string[]): SocialRule[] {
  const offSet = new Set(off);
  const out: SocialRule[] = [];
  const consider = (key: string, phrase: string, rows: readonly PastDecision[]) => {
    if (rows.length < MIN_GROUP_POSTS) return;
    const ok = rows.filter((r) => r.approved).length;
    const share = ok / rows.length;
    if (share >= 0.75) out.push({ key, text: `Posts that ${phrase} get approved. ${ok} of ${rows.length}.`, count: rows.length, on: !offSet.has(key) });
    else if (share <= 0.34) out.push({ key, text: `Posts that ${phrase} are often not for you. ${ok} of ${rows.length} approved.`, count: rows.length, on: !offSet.has(key) });
  };
  for (const h of Object.keys(HOOK_PHRASE) as PostHook[]) consider(`hook:${h}`, HOOK_PHRASE[h], history.filter((p) => p.hook === h));
  for (const f of Object.keys(FORMAT_PHRASE) as PostFormat[]) consider(`format:${f}`, FORMAT_PHRASE[f], history.filter((p) => p.format === f));
  return out;
}

export interface SocialAgentFacts {
  /** Ideas ready from your results and the competitor ads you collected (Semrush ideas are looked up on purpose, so they are not counted). */
  ideas: { ready: number; competitorsWatched: number };
  decisions: { approved: number; notForUs: number };
  rulesOn: number;
  rulesOff: number;
  /** AI-written drafts in the last 30 days. */
  drafts: { count: number; costUsd: number | null; runs: AgentRun[] };
  /** AI pictures for social posts in the last 30 days, and posts still being worked on that have none. */
  pictures: { made: number; costUsd: number | null; withoutPicture: number; runs: AgentRun[] };
  review: { waiting: number; approved: number };
  send: { channelsLinked: number; channelsTotal: number; sent: number; failed: number; runs: AgentRun[] };
  results: { lastOkAt: string | null; lastError: string | null; delivered: number };
  /** Is there at least one enabled channel? */
  hasChannels: boolean;
}

const money = (v: number | null) => (v === null ? "Not available" : `$${v.toFixed(2)}`);
const plural = (n: number, one: string, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;

export function buildSocialAgentSteps(f: SocialAgentFacts): Pick<SocialAgents, "steps" | "waiting"> {
  const steps: SocialStep[] = [];

  steps.push({
    id: "ideas", title: "Find ideas", who: "Idea finder",
    state: f.ideas.ready > 0 ? "you" : "idle",
    did: f.ideas.ready > 0 ? `${plural(f.ideas.ready, "idea")} ready from your results and competitor ads. A manager can add Semrush searches.` : f.ideas.competitorsWatched === 0 ? "No ideas yet. Post a few times and add companies to watch in Ads, or look up a topic in Semrush." : "No new ideas right now. Look up a topic in Semrush for more.",
    cost: "",
    limits: ["Uses your own results, the competitor ads already collected, and Semrush when a manager looks up a topic", "Does not use your articles", "Competitor words are shown for reference and never copied", "A person decides which ideas become posts"],
    runs: [],
    action: null,
  });

  const learned = f.decisions.approved + f.decisions.notForUs;
  steps.push({
    id: "learn", title: "Learn from feedback", who: "Learner",
    state: learned === 0 ? "idle" : "done",
    did: learned === 0 ? "No decisions yet. It learns once you approve or decline posts." : `Learned from ${plural(learned, "decision")}: ${f.decisions.approved} approved, ${f.decisions.notForUs} not for us. ${plural(f.rulesOn, "rule")} on${f.rulesOff ? `, ${f.rulesOff} off` : ""}.`,
    cost: "",
    limits: ["Uses the last 8 weeks of your decisions", "Needs 3 decisions before it writes a rule", "A manager can switch any rule off", "Rules in the on position shape the 1 to 5 score and are given to the AI writer"],
    runs: [],
    action: null,
  });

  steps.push({
    id: "write", title: "Write posts", who: "AI writer",
    state: f.drafts.count > 0 ? "done" : "idle",
    did: f.drafts.count > 0 ? `Wrote ${plural(f.drafts.count, "draft")} in the last 30 days.` : "No AI drafts yet. Start one from a post.",
    cost: money(f.drafts.costUsd),
    limits: ["Writes up to 3 versions per request", "Never adds a figure that is not in the article", "Never adds a web address of its own", "Always a draft. It cannot send anything"],
    runs: f.drafts.runs,
    action: { label: "Write a post", to: "posts" },
  });

  steps.push({
    id: "pictures", title: "Make pictures", who: "AI picture maker",
    state: f.pictures.withoutPicture > 0 ? "you" : f.pictures.made > 0 ? "done" : "idle",
    did: f.pictures.withoutPicture > 0
      ? `${plural(f.pictures.withoutPicture, "post")} in progress ${f.pictures.withoutPicture === 1 ? "has" : "have"} no picture. ${f.pictures.made} AI ${f.pictures.made === 1 ? "picture" : "pictures"} made in the last 30 days.`
      : f.pictures.made > 0 ? `Made ${plural(f.pictures.made, "picture")} in the last 30 days.` : "No AI pictures yet. Open a post and choose Make or add a picture.",
    cost: money(f.pictures.costUsd),
    limits: ["Makes 1 to 4 pictures from a short description", "No words, logos or software screens inside the picture", "Gets a second look before you see it", "You pick the one to use. It is checked again when you review the post"],
    runs: f.pictures.runs,
    action: { label: "Make a picture", to: "posts" },
  });

  steps.push({
    id: "review", title: "You review", who: "You",
    state: f.review.waiting > 0 ? "you" : "done",
    did: f.review.waiting > 0 ? `${plural(f.review.waiting, "post")} waiting for your OK.` : `Nothing waiting. ${f.review.approved} approved and ready.`,
    cost: "",
    limits: ["Every post needs a person's OK", "Whoever wrote a post cannot approve it. A manager can, and it is logged", "Nothing is sent until a time is chosen"],
    runs: [],
    action: f.review.waiting > 0 ? { label: "Review them", to: "review" } : null,
  });

  const sendState = !f.hasChannels ? "off" : f.send.failed > 0 ? "you" : f.send.channelsLinked === 0 ? "you" : "done";
  steps.push({
    id: "send", title: "Send to Sociamonials", who: "Sender",
    state: sendState,
    did: !f.hasChannels ? "No channels are switched on." : f.send.failed > 0 ? `${plural(f.send.failed, "post")} failed to send.` : f.send.channelsLinked === 0 ? "No channel is linked in Sociamonials yet." : `Sent ${f.send.sent} in the last 30 days.`,
    cost: "",
    limits: ["Sends only approved posts", "Sends at the time you chose", "Never posts without Sociamonials accepting it"],
    runs: f.send.runs,
    action: !f.hasChannels || f.send.channelsLinked === 0 ? { label: "Open channels", to: "channels" } : f.send.failed > 0 ? { label: "See failed posts", to: "posts" } : null,
  });

  steps.push({
    id: "results", title: "Read results", who: "Reader",
    state: f.results.lastError ? "you" : f.results.lastOkAt ? "done" : "idle",
    did: f.results.lastError ? `The last check failed: ${f.results.lastError}` : f.results.lastOkAt ? `Checked delivery. ${f.results.delivered} delivered in the last 30 days.` : "Has not checked yet.",
    cost: "",
    limits: ["Reads delivery status from Sociamonials", "Does not read likes, comments or clicks", "Unknown stays Not available"],
    runs: [],
    action: { label: "See results", to: "results" },
  });

  const waiting = f.review.waiting > 0 ? `${plural(f.review.waiting, "post")} waiting for your OK.` : f.send.failed > 0 ? `${plural(f.send.failed, "post")} failed to send.` : null;
  return { steps, waiting };
}
